import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Gift, Plus, Search, Trash2, Euro, X } from 'lucide-react';

const EUR = (v) => `${Number(v || 0).toFixed(2)} €`;

const STATUS_META = {
  active: { label: 'Aktiv', cls: 'bg-[#16A34A]/15 text-[#16A34A]' },
  redeemed: { label: 'Eingelöst', cls: 'bg-[#6B7280]/15 text-[#6B7280]' },
  expired: { label: 'Abgelaufen', cls: 'bg-[#DC2626]/15 text-[#DC2626]' },
};

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d + 'T00:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
}

export default function AdminVouchers() {
  const [vouchers, setVouchers] = useState([]);
  const [summary, setSummary] = useState({ total: 0, total_value: 0, open_value: 0 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: '', amount: '', guest_email: '', guest_name: '', valid_until: '', note: '' });
  const [saving, setSaving] = useState(false);
  const [redeemFor, setRedeemFor] = useState(null);
  const [redeemAmount, setRedeemAmount] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/vouchers', { params: { search, status: statusFilter } });
      setVouchers(r.data?.vouchers || []);
      setSummary({ total: r.data?.total || 0, total_value: r.data?.total_value || 0, open_value: r.data?.open_value || 0 });
    } catch {
      toast.error('Gutscheine konnten nicht geladen werden.');
    } finally { setLoading(false); }
  }, [search, statusFilter]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const submit = async () => {
    if (!form.code.trim()) return toast.error('Bitte die Gutschein-Nummer eingeben.');
    if (!form.guest_email.trim()) return toast.error('Bitte die E-Mail des Gasts eingeben.');
    if (!(Number(form.amount) > 0)) return toast.error('Bitte einen Betrag größer 0 eingeben.');
    setSaving(true);
    try {
      await api.post('/vouchers', {
        code: form.code.trim(),
        amount: Number(form.amount),
        guest_email: form.guest_email.trim(),
        guest_name: form.guest_name.trim(),
        valid_until: form.valid_until || null,
        note: form.note.trim(),
      });
      toast.success('Gutschein angelegt.');
      setShowForm(false);
      setForm({ code: '', amount: '', guest_email: '', guest_name: '', valid_until: '', note: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gutschein konnte nicht angelegt werden.');
    } finally { setSaving(false); }
  };

  const doRedeem = async () => {
    if (!(Number(redeemAmount) > 0)) return toast.error('Bitte einen Betrag eingeben.');
    try {
      await api.post(`/vouchers/${encodeURIComponent(redeemFor.code)}/redeem`, { amount: Number(redeemAmount) });
      toast.success('Gutschein eingelöst.');
      setRedeemFor(null); setRedeemAmount(''); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Einlösen fehlgeschlagen.');
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Gutschein ${v.code} wirklich löschen?`)) return;
    try { await api.delete(`/vouchers/${v.id}`); toast.success('Gutschein gelöscht.'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen.'); }
  };

  return (
    <div className="space-y-6" data-testid="admin-vouchers">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-brand flex items-center gap-2"><Gift className="w-6 h-6 text-[#EAB308]" /> Gutscheine</h1>
          <p className="text-sm text-[#5C5C59] mt-1">Gutscheine der Kasse erfassen und einem Gast per E-Mail zuordnen.</p>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="voucher-add-btn" className="btn-primary px-4 py-2 rounded flex items-center gap-2">
          <Plus className="w-4 h-4" /> Gutschein anlegen
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-4"><div className="text-xs text-[#5C5C59]">Gutscheine</div><div className="text-2xl font-semibold" data-testid="voucher-count">{summary.total}</div></div>
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-4"><div className="text-xs text-[#5C5C59]">Gesamtwert</div><div className="text-2xl font-semibold">{EUR(summary.total_value)}</div></div>
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-4"><div className="text-xs text-[#5C5C59]">Offener Restwert</div><div className="text-2xl font-semibold text-[#16A34A]">{EUR(summary.open_value)}</div></div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input value={search} onChange={e => setSearch(e.target.value)} data-testid="voucher-search" placeholder="Nummer, E-Mail oder Name suchen…" className="w-full pl-9 pr-3 py-2 border border-[#E5E5E0] rounded" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="voucher-status-filter" className="px-3 py-2 border border-[#E5E5E0] rounded bg-white">
          <option value="">Alle Status</option>
          <option value="active">Aktiv</option>
          <option value="redeemed">Eingelöst</option>
          <option value="expired">Abgelaufen</option>
        </select>
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-[#9CA3AF]">Lädt…</div>
        ) : vouchers.length === 0 ? (
          <div className="p-8 text-center text-[#9CA3AF]" data-testid="voucher-empty">Keine Gutscheine gefunden.</div>
        ) : (
          <div className="divide-y divide-[#F0F0EC]">
            {vouchers.map(v => {
              const meta = STATUS_META[v.status] || STATUS_META.active;
              return (
                <div key={v.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`voucher-row-${v.code}`}>
                  <div className="flex-1 min-w-[180px]">
                    <div className="font-mono font-semibold text-[#1C1C19]">{v.code}</div>
                    <div className="text-sm text-[#5C5C59]">{v.guest_name || '—'} · {v.guest_email}</div>
                    {v.note && <div className="text-xs text-[#9CA3AF] mt-0.5">{v.note}</div>}
                  </div>
                  <div className="text-right">
                    <div className="font-semibold">{EUR(v.remaining)} <span className="text-xs text-[#9CA3AF] font-normal">/ {EUR(v.amount)}</span></div>
                    <div className="text-xs text-[#9CA3AF]">gültig bis {fmtDate(v.valid_until)}</div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded font-medium ${meta.cls}`}>{meta.label}</span>
                  <div className="flex items-center gap-2">
                    {v.status === 'active' && (
                      <button onClick={() => { setRedeemFor(v); setRedeemAmount(String(v.remaining)); }} data-testid={`voucher-redeem-${v.code}`} className="px-3 py-1.5 text-sm rounded border border-[#16A34A] text-[#16A34A] hover:bg-[#16A34A]/10 flex items-center gap-1">
                        <Euro className="w-3.5 h-3.5" /> Einlösen
                      </button>
                    )}
                    <button onClick={() => remove(v)} data-testid={`voucher-delete-${v.code}`} className="p-1.5 text-[#DC2626] hover:bg-[#DC2626]/10 rounded"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-[#1C1C19]/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="voucher-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium">Neuer Gutschein</h2>
              <button onClick={() => setShowForm(false)} className="text-[#9CA3AF] hover:text-[#1C1C19]"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium block mb-1">Gutschein-Nummer *</label>
                <input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} data-testid="voucher-form-code" placeholder="z. B. LC-1001" className="w-full px-3 py-2 border border-[#E5E5E0] rounded" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Betrag (€) *</label>
                <input type="number" min="0" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="voucher-form-amount" placeholder="z. B. 50" className="w-full px-3 py-2 border border-[#E5E5E0] rounded" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">E-Mail des Gasts *</label>
                <input type="email" value={form.guest_email} onChange={e => setForm({ ...form, guest_email: e.target.value })} data-testid="voucher-form-email" placeholder="gast@example.com" className="w-full px-3 py-2 border border-[#E5E5E0] rounded" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Name des Gasts</label>
                <input value={form.guest_name} onChange={e => setForm({ ...form, guest_name: e.target.value })} data-testid="voucher-form-name" placeholder="optional" className="w-full px-3 py-2 border border-[#E5E5E0] rounded" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Gültig bis</label>
                <input type="date" value={form.valid_until} onChange={e => setForm({ ...form, valid_until: e.target.value })} data-testid="voucher-form-valid" className="w-full px-3 py-2 border border-[#E5E5E0] rounded" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Notiz</label>
                <input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} data-testid="voucher-form-note" placeholder="optional" className="w-full px-3 py-2 border border-[#E5E5E0] rounded" />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowForm(false)} className="flex-1 py-2 rounded border border-[#E5E5E0]">Abbrechen</button>
              <button onClick={submit} disabled={saving} data-testid="voucher-form-save" className="flex-1 btn-primary py-2 rounded">{saving ? 'Speichert…' : 'Anlegen'}</button>
            </div>
          </div>
        </div>
      )}

      {redeemFor && (
        <div className="fixed inset-0 bg-[#1C1C19]/40 z-50 flex items-center justify-center p-4" onClick={() => setRedeemFor(null)}>
          <div className="bg-white rounded-lg max-w-sm w-full p-6" onClick={e => e.stopPropagation()} data-testid="voucher-redeem-form">
            <h2 className="text-lg font-medium mb-1">Gutschein einlösen</h2>
            <p className="text-sm text-[#5C5C59] mb-4">{redeemFor.code} · Restwert {EUR(redeemFor.remaining)}</p>
            <label className="text-sm font-medium block mb-1">Einzulösender Betrag (€)</label>
            <input type="number" min="0" step="0.01" max={redeemFor.remaining} value={redeemAmount} onChange={e => setRedeemAmount(e.target.value)} data-testid="voucher-redeem-amount" className="w-full px-3 py-2 border border-[#E5E5E0] rounded" />
            <div className="flex gap-2 mt-5">
              <button onClick={() => setRedeemFor(null)} className="flex-1 py-2 rounded border border-[#E5E5E0]">Abbrechen</button>
              <button onClick={doRedeem} data-testid="voucher-redeem-confirm" className="flex-1 btn-primary py-2 rounded">Einlösen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
